/**
 * CAD Board - the 911 daily activity board.
 *
 * Two panes: the call log on the left (color-coded rows - Received = red,
 * Dispatched = amber, Completed = muted, matching the legacy 911CAD color
 * language) and a unit rail on the right (active personnel with their current
 * post). Auto-refreshes every 15s. "Add Call" opens the call card modal; a row
 * click opens that call for editing.
 */
import { useEffect, useState, useMemo } from 'react'
import { useCadStore, type CadCall } from '../../stores/cad'
import { useDesktopStore } from '../../stores/desktop'
import CallCardModal from './CallCardModal'

function statusColor(status: string): string {
  if (status === 'Received') return '#e05a5a'
  if (status === 'Dispatched') return '#e0a23a'
  return 'var(--text-muted)'
}

function fullStamp(ts: string): string {
  // received_ts is "YYYY-MM-DD HH:MM:SS" (ET) → show as "MM/DD/YYYY HH:MM:SS"
  if (!ts) return ''
  const [d, t] = ts.split(' ')
  if (!d) return ts
  const [y, m, day] = d.split('-')
  return `${m}/${day}/${y}${t ? ' ' + t : ''}`
}

export default function CadBoard() {
  const { calls, loading, loadCalls, loadRefs, roster } = useCadStore()
  const personnel = useDesktopStore(s => s.personnel)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<CadCall | null>(null)

  useEffect(() => {
    loadRefs()
    loadCalls()
    const iv = setInterval(() => loadCalls(), 15_000)
    return () => clearInterval(iv)
  }, [loadCalls, loadRefs])

  // Every officer from the roster, each marked on-duty or not. On duty = present
  // in today's personnel with a name. Toggling is handled in the rail below.
  const units = useMemo(() => {
    const onDuty = new Set<string>()
    personnel.forEach(p => {
      if (p.name && p.callSign) onDuty.add(p.callSign.toUpperCase())
    })
    return roster
      .map(r => ({ cs: r.call_sign, name: r.name, rank: r.rank, onDuty: onDuty.has((r.call_sign || '').toUpperCase()) }))
      .sort((a, b) => (a.onDuty === b.onDuty ? a.cs.localeCompare(b.cs, undefined, { numeric: true }) : a.onDuty ? -1 : 1))
  }, [roster, personnel])

  const onDutyCount = units.filter(u => u.onDuty).length

  // Toggle an officer on/off duty straight from the rail. On = add to personnel
  // in the first empty officer slot; Off = clear their slot by identity. Both
  // go through the desktop store so the duty board stays in sync.
  const { addOfficerByCallSign, clearSlotByIdentity } = useDesktopStore()
  function toggleDuty(u: { cs: string; name: string; onDuty: boolean }) {
    if (u.onDuty) clearSlotByIdentity(u.cs, u.name)
    else addOfficerByCallSign(u.cs, u.name)
  }

  function openNew() { setEditing(null); setModalOpen(true) }
  function openEdit(c: CadCall) { setEditing(c); setModalOpen(true) }

  const th: React.CSSProperties = { textAlign: 'left', padding: '8px 10px', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text-muted)', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, background: 'var(--bg-surface)' }
  const td: React.CSSProperties = { padding: '8px 10px', fontSize: 13, borderBottom: '1px solid var(--border)' }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0 12px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 20, color: 'var(--text-primary)' }}>Gatehouse</h1>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Daily activity board &middot; 911 CAD</div>
        </div>
        <button onClick={openNew} style={{ padding: '9px 16px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
          + Add Call
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 240px', gap: 12, flex: 1, minHeight: 0 }}>
        <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 10, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={th}>Date / Time</th>
                  <th style={th}>Type Call</th>
                  <th style={th}>Location</th>
                  <th style={th}>Unit</th>
                  <th style={th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {calls.map((c, i) => (
                  <tr key={c.id} onClick={() => openEdit(c)} style={{ cursor: 'pointer', background: i % 2 === 1 ? 'var(--bg-elevated)' : 'transparent' }}>
                    <td style={{ ...td, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{fullStamp(c.received_ts)}</td>
                    <td style={{ ...td, color: statusColor(c.status), fontWeight: c.status === 'Received' ? 600 : 400 }}>
                      {c.flagged_for_incident ? '\u2605 ' : ''}{c.type_call}
                    </td>
                    <td style={{ ...td, color: 'var(--text-primary)' }}>{c.business_name || c.location || '-'}</td>
                    <td style={{ ...td, color: 'var(--text-primary)' }}>{c.unit_assigned || '-'}</td>
                    <td style={{ ...td, color: statusColor(c.status) }}>{c.status}</td>
                  </tr>
                ))}
                {calls.length === 0 && !loading && (
                  <tr><td colSpan={5} style={{ ...td, textAlign: 'center', color: 'var(--text-muted)', padding: 30 }}>No calls yet. Click "Add Call" to log the first one.</td></tr>
                )}
                {loading && calls.length === 0 && (
                  <tr><td colSpan={5} style={{ ...td, textAlign: 'center', color: 'var(--text-muted)', padding: 30 }}>Loading...</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 10, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div style={{ padding: '10px 12px', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text-muted)', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between' }}>
            <span>Units</span>
            <span style={{ color: '#3fae6a' }}>{onDutyCount} on duty</span>
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {units.map(u => (
              <div key={u.cs} onClick={() => toggleDuty(u)} title={u.onDuty ? 'Click to set off duty' : 'Click to set on duty'} style={{
                padding: '8px 12px', borderBottom: '1px solid var(--border)',
                display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer',
                background: u.onDuty ? 'rgba(63,174,106,0.07)' : 'transparent',
                opacity: u.onDuty ? 1 : 0.45,
              }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                  background: u.onDuty ? '#3fae6a' : 'var(--text-muted)',
                  boxShadow: u.onDuty ? '0 0 5px rgba(63,174,106,0.8)' : 'none' }} />
                <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 600 }}>
                  {u.cs}<span style={{ fontWeight: 400, color: 'var(--text-muted)', marginLeft: 10 }}>{u.name}</span>
                </div>
              </div>
            ))}
            {units.length === 0 && <div style={{ padding: 16, fontSize: 12, color: 'var(--text-muted)' }}>No roster loaded.</div>}
          </div>
        </div>
      </div>

      {modalOpen && <CallCardModal call={editing} onClose={() => { setModalOpen(false); loadCalls() }} />}
    </div>
  )
}
